

import React, { useEffect, useState, useContext } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { AuthContext, useMaterialUIController, setLayout } from "context";
import AuthService from "services/auth-service";
import WorkspaceService from "services/workspace-service";

import RequireEmailVerified from "./RequireEmailVerified";
import RequireWorkspace from "./RequireWorkspace";
import RequireOwnProfile from "./RequireOwnProfile";
import RequireActiveSubscription from "./RequireActiveSubscription";
import RequireSubscriptionUsage from "./RequireSubscriptionUsage";
import RequireSubscriptionNotExpired from "./RequireSubscriptionNotExpired";
import RequireInviteStep from "./RequireInviteStep"; // [NEW] Onboarding invite step

const ProtectedRoute = ({ isAuthenticated, children }) => {
  const { isAuthenticated: loggedIn } = useContext(AuthContext);
  const location = useLocation();
  const [controller, dispatch] = useMaterialUIController();
  const { layout } = controller;

  // States follow monolithic pattern
  const [isVerified, setIsVerified] = useState(null);
  const [user, setUser] = useState(null); // [NEW] Store full user object
  const [workspaceLoading, setWorkspaceLoading] = useState(true);
  const [hasWorkspace, setHasWorkspace] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [hasOwnProfiles, setHasOwnProfiles] = useState(null);
  const [userRole, setUserRole] = useState(null); // [NEW] Track Role

  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [activeSubscription, setActiveSubscription] = useState(null);

  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // -----------------------------------------------------------
  // 0. Force Dashboard Layout
  // -----------------------------------------------------------
  useEffect(() => {
    if (loggedIn && layout !== "dashboard") {
      setLayout(dispatch, "dashboard");
    }
  }, [loggedIn, layout, dispatch]);

  // -----------------------------------------------------------
  // [NEW] Workspace Switch Listener (Soft Refresh Security)
  // -----------------------------------------------------------
  useEffect(() => {
    const onProfileUpdated = () => {
      // Reset all validation states to trigger re-checks for the new workspace
      setWorkspaceLoading(true);
      setHasWorkspace(null);
      setProfileLoading(true);
      setHasOwnProfiles(null);
      setSubscriptionLoading(true);
      setActiveSubscription(null);
    };

    window.addEventListener("profile-updated", onProfileUpdated);
    return () => window.removeEventListener("profile-updated", onProfileUpdated);
  }, []);

  // -----------------------------------------------------------
  // [NEW] Track Initial Load Completion
  // -----------------------------------------------------------
  useEffect(() => {
    // Only mark complete if we've successfully passed the first "verified" load
    if (isVerified === true && !workspaceLoading && !subscriptionLoading && !profileLoading) {
      setInitialLoadComplete((prev) => (prev ? prev : true));
    }
    // If not verified or other terminal states, we don't mark "complete" to ensure first boot safety
  }, [isVerified, workspaceLoading, subscriptionLoading, profileLoading]);

  // -----------------------------------------------------------
  // 1. Email Verification Check
  // -----------------------------------------------------------
  useEffect(() => {
    if (!loggedIn) {
      setIsVerified(null);
      return;
    }

    const fetchProfile = async () => {
      try {
        const resp = await AuthService.getProfile();
        const userData = resp.data.attributes;
        setIsVerified(!!userData.email_verified_at);
        setUserRole(userData.role); // [NEW] Save Role
        setUser(userData); // [NEW] Save full user object
      } catch {
        setIsVerified(false);
      }
    };

    fetchProfile();
  }, [loggedIn]);

  // -----------------------------------------------------------
  // 1.5. Workspace & Profile Status Check
  // -----------------------------------------------------------
  useEffect(() => {
    // Wait for isVerified to be resolved to true
    if (!loggedIn || isVerified !== true || hasWorkspace !== null) return;

    const fetchWorkspaceStatus = async () => {
      // [NEW] Demo User Bypass
      if (userRole === "demo_viewer") {
        try {
          const status = await WorkspaceService.checkWorkspaceStatus();
          if (status.workspace) {
            localStorage.setItem("activeWorkspaceId", status.workspace);
          }
        } catch (e) {
          console.error("Demo check status error:", e);
        }
        setHasWorkspace(true);
        setHasOwnProfiles(true);
        setWorkspaceLoading(false);
        setProfileLoading(false);
        return;
      }

      try {
        const status = await WorkspaceService.checkWorkspaceStatus();
        setHasWorkspace(status.hasWorkspace);
        setHasOwnProfiles(status.hasProfiles);

        // [NEW] Persist activeWorkspaceId to localStorage for subsequent API calls
        if (status.workspace) {
          localStorage.setItem("activeWorkspaceId", status.workspace);
        }
      } catch (error) {
        console.error("Error checking workspace status:", error);
        // Fallback for unexpected errors
        setHasWorkspace(true);
        setHasOwnProfiles(true);
      } finally {
        setWorkspaceLoading(false);
        setProfileLoading(false);
      }
    };

    fetchWorkspaceStatus();
  }, [loggedIn, isVerified, hasWorkspace, userRole]);

  // -----------------------------------------------------------
  // 3. Subscription Check
  // -----------------------------------------------------------
  useEffect(() => {
    // Wait for hasWorkspace and hasOwnProfiles to be resolved
    if (!loggedIn || hasWorkspace !== true || hasOwnProfiles === null || !subscriptionLoading) return;

    // Using subscriptionLoading to control this effect run ensures we don't spam fetch. 
    // Ideally we should use a "hasFetchedSubscription" ref or just trust loading state.
    // The previous code checked `hasActiveSubscription !== null`. 
    // Since we now use `activeSubscription` (object) which can be null if not found, 
    // checking `activeSubscription !== null` fails for the negative case. 
    // We should rely on `subscriptionLoading`.

    // NOTE: The previous code was:  if (!loggedIn || hasOwnProfiles !== true || hasActiveSubscription !== null) return;
    // But subscriptionLoading was initialized to true. So when it finishes, it sets loading false.
    // So checking `hasActiveSubscription !== null` was the guard.
    // Let's rely on `subscriptionLoading` being TRUE to run the fetch.

    // Actually, to make it robust: 
    // Run if subscriptionLoading is true.

    if (!subscriptionLoading) return;

    const fetchSubscription = async () => {
      // [NEW] Demo User Bypass: Always give them a valid "dummy" subscription
      if (userRole === "demo_viewer") {
        setActiveSubscription({
          status: "ACTIVE",
          next_renewal_date: new Date(new Date().setFullYear(new Date().getFullYear() + 1)) // 1 year from now
        });
        setSubscriptionLoading(false);
        return;
      }

      try {
        const subs = await WorkspaceService.fetchWorkspaceSubscription();
        // [NEW] Defensive check for array and status existence
        const activeSub = Array.isArray(subs)
          ? subs.find((s) => s.status && s.status.toUpperCase() === "ACTIVE")
          : null;
        setActiveSubscription(activeSub || null);
      } catch {
        setActiveSubscription(null);
      } finally {
        setSubscriptionLoading(false);
      }
    };

    fetchSubscription();
  }, [loggedIn, hasWorkspace, hasOwnProfiles, subscriptionLoading, userRole]);

  // -----------------------------------------------------------
  // Primary Render and Gating
  // -----------------------------------------------------------
  const isOAuthResponsePage =
    location.pathname.startsWith("/connect/profile/response") ||
    location.pathname.startsWith("/Youtube-");

  const isPaymentSuccessOrProfile =
    location.pathname.startsWith("/payment/success") ||
    location.pathname.startsWith("/payment/fail");

  if (isOAuthResponsePage || isPaymentSuccessOrProfile) return children;

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  // Stabilization Check: Block content rendering until the last step is resolved
  // We check loading flags or explicit null states for stability.

  // 1. First, wait for verification check
  if (isVerified === null) {
    return null;
  }

  // 2. Dependency-aware stabilization:
  // ONLY block rendering if this is the INITIAL load.
  // For soft refreshes (initialLoadComplete === true), we KEEP rendering children (and their guards)
  // while the background re-validation occurs.
  if (!initialLoadComplete && isVerified === true) {
    if (workspaceLoading) return null;
    if (hasWorkspace === true && (subscriptionLoading || profileLoading)) return null;
  }

  // Gating Components Chain
  // [MODIFIED] Reordered: Subscription checks happen BEFORE Profile checks.
  return (
    <RequireEmailVerified
      isVerified={isVerified}
      initialLoadComplete={initialLoadComplete}
    >
      <RequireWorkspace
        hasWorkspace={hasWorkspace}
        loading={workspaceLoading}
        user={user}
        initialLoadComplete={initialLoadComplete}
      >
        {isPaymentSuccessOrProfile ? (
          children
        ) : (
          <RequireActiveSubscription
            hasOwnProfiles={hasOwnProfiles}
            hasActiveSubscription={!!activeSubscription}
            loading={subscriptionLoading}
            initialLoadComplete={initialLoadComplete}
          >
            <RequireSubscriptionNotExpired
              activeSubscription={activeSubscription}
              initialLoadComplete={initialLoadComplete}
            >
              <RequireOwnProfile
                isVerified={isVerified}
                hasOwnProfiles={hasOwnProfiles}
                userRole={userRole}
                loading={profileLoading}
                initialLoadComplete={initialLoadComplete}
              >
                <RequireInviteStep>
                  <RequireSubscriptionUsage
                    hasActiveSubscription={!!activeSubscription}
                    hasOwnProfiles={hasOwnProfiles}
                    initialLoadComplete={initialLoadComplete}
                  >
                    {children}
                  </RequireSubscriptionUsage>
                </RequireInviteStep>
              </RequireOwnProfile>
            </RequireSubscriptionNotExpired>
          </RequireActiveSubscription>
        )}
      </RequireWorkspace>
    </RequireEmailVerified>
  );
};

export default ProtectedRoute;